import React from 'react';
import { motion } from 'framer-motion';
import { HiCode } from 'react-icons/hi';

const skillCategories = [
  {
    category: 'Frontend',
    skills: ['HTML5', 'CSS3', 'JavaScript (ES6+)', 'TypeScript', 'React.js', 'Next.js'],
    color: 'from-navy-500 to-navy-700',
  },
  {
    category: 'Backend',
    skills: ['Node.js', 'Deno', 'Express.js', 'Nest.js',  'Microservices'],
    color: 'from-beige-500 to-beige-700',
  },
  {
    category: 'Database',
    skills: ['PostgreSQL', 'MySQL', 'MongoDB'],
    color: 'from-charcoal-600 to-charcoal-800',
  },
  {
    category: 'Tools & APIs',
    skills: ['Git', 'GraphQL', 'REST APIs', 'Shopify', 'WordPress', 'Jest', 'Vitest'],
    color: 'from-navy-600 to-navy-800',
  },
  {
    category: 'Cloud & PM',
    skills: ['GCP', 'AWS', 'Agile', 'Scrum'],
    color: 'from-beige-600 to-beige-800',
  },
];

const Skills = () => {
  return (
    <section id="skills" className="py-12 sm:py-16 md:py-20 bg-white relative overflow-hidden">
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-1/3 right-0 w-64 h-64 sm:w-80 sm:h-80 md:w-96 md:h-96 bg-navy-400 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/3 left-0 w-64 h-64 sm:w-80 sm:h-80 md:w-96 md:h-96 bg-beige-400 rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 md:px-8 lg:px-12 xl:px-20 relative z-10">
        <motion.div
          className="text-center mb-10 sm:mb-12 md:mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span className="text-beige-600 font-semibold text-xs sm:text-sm uppercase tracking-wider">
            Technical Expertise
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-navy-900 mt-2 font-primary">
            Skills & Technologies
          </h2>
          <div className="w-16 sm:w-20 h-1 bg-beige-500 mx-auto mt-3 sm:mt-4"></div>
          <p className="mt-4 sm:mt-6 text-gray-600 max-w-2xl mx-auto text-sm sm:text-base px-4">
            My toolkit includes modern technologies and frameworks for developing scalable and dynamic applications.
          </p>
        </motion.div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 max-w-6xl mx-auto">
          {skillCategories.map((category, catIndex) => (
            <motion.div
              key={catIndex}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: catIndex * 0.1 }}
              className="bg-gradient-to-br from-gray-50 to-white p-4 sm:p-6 rounded-xl sm:rounded-2xl shadow-lg border border-gray-200 hover:shadow-xl transition-all duration-300 group"
            >
              <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
                <div className={`p-2 sm:p-3 rounded-lg sm:rounded-xl bg-gradient-to-br ${category.color} shadow-md`}>
                  <HiCode className="text-xl sm:text-2xl text-white" />
                </div>
                <h3 className="text-base sm:text-lg md:text-xl font-bold text-navy-900 font-primary group-hover:text-navy-700 transition-colors">
                  {category.category}
                </h3>
              </div>

              <div className="flex flex-wrap gap-2">
                {category.skills.map((skill, skillIndex) => (
                  <motion.span
                    key={skillIndex}
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: (catIndex * 0.1) + (skillIndex * 0.05) }}
                    className="px-2.5 py-1.5 sm:px-3 sm:py-2 bg-white border border-gray-300 text-gray-700 rounded-lg text-xs sm:text-sm font-medium hover:border-beige-500 hover:bg-beige-50 hover:text-navy-900 transition-all cursor-default shadow-sm"
                  >
                    {skill}
                  </motion.span>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
        <motion.div
          className="mt-10 sm:mt-12 md:mt-16 text-center bg-gradient-to-r from-navy-800 to-navy-900 p-6 sm:p-8 rounded-xl sm:rounded-2xl shadow-xl max-w-4xl mx-auto"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h3 className="text-xl sm:text-2xl font-bold text-beige-300 mb-3 sm:mb-4 font-primary">
            Always Learning, Always Growing
          </h3>
          <p className="text-gray-300 leading-relaxed text-sm sm:text-base">
            Technology evolves rapidly, and I'm committed to continuous learning. I regularly explore new frameworks,
            best practices, and emerging technologies to deliver cutting-edge solutions.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default Skills;
